/**
 * CASTLE ATLAS — Copywriting Skill Config
 * ─────────────────────────────────────────────────
 * This file owns ALL visual logic for the Copywriting skill card.
 * Supabase owns content (name, desc, lessons, exercises, colors).
 * This file owns rendering (JSX, animations, SVG card structure).
 *
 * REBUILT (this pass) — this was the file with the bugs flagged in
 * the dashboard/skills audit:
 *   - All text (title, description, status badge, CTA label) was
 *     rendered as SVG <text> inside a fixed 340×400 viewBox, so on
 *     mobile it scaled down with the box instead of reflowing.
 *   - The description was hard-truncated with .slice(46), cutting
 *     words mid-word rather than wrapping.
 *   - The invisible click overlay used percentage positioning
 *     (bottom/left/right in %, height in px) that only lined up with
 *     the drawn SVG button at specific aspect ratios.
 *
 * Fixed by keeping the hand-sketched border/notebook-lines as a
 * decorative, pointer-events:none SVG behind the content, and putting
 * all text in real HTML — same prop interface, same visual identity,
 * now reflows at any width and the CTA is a real element instead of
 * a coordinate guess. Also added the same keyboard-accessibility fix
 * as the other two configs.
 *
 * Used by:
 *   - Dashboard.jsx (active/progress card)
 *   - Skills.jsx (preview card)
 *
 * Drop path: frontend/src/data/configs/config.copywriting.js
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/* ── Design tokens ── */
export const TOKENS = {
  accent:     '#7c3aed',
  accentDark: '#a78bfa',
  trans:      'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
  fast:       'all 0.18s ease',
  radius:     24,
  fontSkill:  'var(--font-skill-copy)', // DM Serif Display — works in JSX style props
};

/* ── Theme-aware helpers ── */
const theme = (isDark) => ({
  paperBg: isDark ? '#1c1810' : '#fefce8',
  inkCol:  isDark ? '#d4c89a' : '#2d2008',
  pen:     isDark ? '#c4b87a' : '#3d2e08',
  faint:   isDark ? 'rgba(196,184,122,0.14)' : 'rgba(61,46,8,0.12)',
  accent:  isDark ? '#a78bfa' : '#7c3aed',
  muted:   isDark ? '#78716c' : '#8c7a4a',
  desc:    isDark ? '#a89968' : '#5c4a1e',
});

/* ── Sketchy rectangle path generator — decorative only, never text ── */
const sk = (x, y, w, h, s) => {
  const j = (n, k) => n + ((k * 7 + 3) % 5) - 2;
  const [x1,y1] = [j(x,s),   j(y,s+1)];
  const [x2,y2] = [j(x+w,s+2), j(y,s+3)];
  const [x3,y3] = [j(x+w,s+4), j(y+h,s+5)];
  const [x4,y4] = [j(x,s+6),   j(y+h,s+7)];
  return `M${x1},${y1} Q${(x1+x2)/2},${y1+((s*11)%4)-2} ${x2},${y2} Q${x3+1},${(y2+y3)/2} ${x3},${y3} Q${(x3+x4)/2},${y3+2} ${x4},${y4} Q${x1-2},${(y4+y1)/2} ${x1},${y1} Z`;
};

/**
 * CopywritingCard
 *
 * Props (unchanged from before):
 *   skill         {object}  — row from Supabase skills table
 *   progress      {number}  — exercises completed by this user
 *   isDark        {boolean} — current theme
 *   mode          {string}  — 'dashboard' | 'skills'
 *   onClick       {fn}      — card click handler (navigate to landing)
 *   onAction      {fn}      — CTA button click
 */
export const CopywritingCard = ({
  skill,
  progress = 0,
  isDark = false,
  mode = 'skills',
  onClick,
  onAction,
}) => {
  const [hovered, setHovered] = useState(false);
  const t = theme(isDark);

  const totalExercises = skill?.exercises ?? 25;
  const pct = totalExercises > 0 ? Math.round((progress / totalExercises) * 100) : 0;

  const wrapperProps = onClick
    ? {
        onClick,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
      }
    : {};

  return (
    <div
      {...wrapperProps}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: t.paperBg,
        borderRadius: TOKENS.radius,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: TOKENS.trans,
        transform: hovered ? 'translateY(-4px) rotate(0deg)' : 'rotate(-0.3deg)',
        boxShadow: hovered ? `0 20px 44px ${t.accent}22` : 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Decorative hand-drawn frame + notebook lines — behind the content,
          pointer-events:none so it can never intercept clicks */}
      <svg viewBox="0 0 340 420" preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        aria-hidden="true">
        <path d="M9,11 Q102,7 204,8 Q272,7 331,10 Q335,140 334,252 Q335,346 332,410 Q238,414 136,411 Q68,412 10,406 Q7,294 6,168 Q7,80 9,11 Z"
          stroke={t.pen} fill="none" strokeWidth={1.5} strokeLinecap="round" opacity={0.55} />
        <path d="M10,22 Q9,210 10.5,390" stroke={isDark ? 'rgba(196,184,122,0.5)' : '#d4b96a'} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        {[100, 150, 200, 250, 300, 350].map((y) => (
          <line key={y} x1={22} y1={y} x2={324} y2={y} stroke={t.faint} strokeWidth="0.5" />
        ))}
      </svg>

      {/* Real, reflowing HTML content on top */}
      <div style={{ position: 'relative', zIndex: 2, padding: '20px 26px 0 44px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: t.muted, textTransform: 'uppercase', marginBottom: 8 }}>
          {skill?.category ?? 'MARKETING'}
        </div>
        <h3 style={{ fontFamily: TOKENS.fontSkill, fontSize: 20, fontWeight: 900, color: t.inkCol, margin: '0 0 4px', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
          {skill?.name ?? 'Copywriting'}
        </h3>
        <p style={{ fontSize: 11.5, color: t.muted, fontStyle: 'italic', margin: '0 0 14px' }}>
          {skill?.tagline ?? 'Write copy that converts. Scored by AI.'}
        </p>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: t.muted }}>{progress} of {totalExercises} exercises</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: t.accent }}>{pct}%</span>
        </div>
        <div style={{ height: 9, borderRadius: 5, border: `0.8px solid ${t.pen}`, opacity: 0.7, overflow: 'hidden', marginBottom: 16 }}>
          {pct > 0 && <div style={{ width: `${pct}%`, height: '100%', background: isDark ? 'rgba(167,139,250,0.28)' : 'rgba(124,58,237,0.22)' }} />}
        </div>

        <div style={{ display: 'inline-flex', alignSelf: 'flex-start', padding: '4px 12px', borderRadius: 3, border: `1px solid ${t.accent}99`, marginBottom: 14 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: t.accent, letterSpacing: '0.06em' }}>
            {pct === 0 ? 'NOT STARTED' : pct === 100 ? 'COMPLETE ✓' : 'IN PROGRESS'}
          </span>
        </div>

        <p style={{ fontSize: 12.5, color: t.desc, lineHeight: 1.65, margin: '0 0 14px' }}>{skill?.description}</p>

        {/* Hand-sketched exercise dots — irregular border-radius per dot fakes
            a drawn ellipse without SVG text (each one stays a real, reflow-safe
            HTML element) */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {Array.from({ length: totalExercises }).map((_, i) => {
            const filled = i < progress;
            const br = `${40 + ((i * 13) % 20)}% ${60 - ((i * 7) % 15)}% ${50 + ((i * 5) % 18)}% ${45 + ((i * 9) % 22)}%`;
            return (
              <div key={i} style={{
                width: 11, height: 10, borderRadius: br,
                border: `1.2px solid ${filled ? t.accent : t.pen}`,
                background: filled ? (isDark ? 'rgba(167,139,250,0.16)' : 'rgba(124,58,237,0.1)') : 'transparent',
                opacity: filled ? 1 : 0.4,
                transition: 'all 0.3s ease',
              }} />
            );
          })}
        </div>

        <p style={{ fontSize: 10.5, color: t.muted, fontStyle: 'italic', margin: '0 0 16px' }}>
          {skill?.lessons ?? 5} modules · {totalExercises} exercises · AI-scored
        </p>

        {/* Margin note — script-font, reacts to progress state, fills the
            space the pencil icon used to occupy */}
        <p style={{
          fontFamily: 'var(--font-script, "Caveat", cursive)', fontSize: 16, color: t.accent,
          opacity: 0.8, transform: 'rotate(-2deg)', display: 'inline-block',
          margin: '0 0 18px', flex: 1,
        }}>
          {pct === 0 ? 'where do I even start?' : pct === 100 ? 'nailed it.' : 'keep the pen moving...'}
        </p>
      </div>

      {/* ── CTA — hand-drawn button: real element for text/clicks, a wobbly
             SVG stroke drawn on top as the "border" instead of a straight
             CSS border. preserveAspectRatio="none" + percentage sizing means
             the squiggle always matches the button's real rendered size —
             no fixed-viewBox math to drift out of sync with. ── */}
      <div style={{ position: 'relative', zIndex: 2, padding: '0 26px 22px 44px', flexShrink: 0 }}>
        {(() => {
          const Wrapper = onAction ? 'button' : Link;
          const wrapperExtra = onAction
            ? { onClick: (e) => { e.stopPropagation(); onAction(); } }
            : { to: skill?.route ?? '/skills/copywriting' };
          return (
            <Wrapper
              {...wrapperExtra}
              style={{
                position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                width: '100%', minHeight: 44, boxSizing: 'border-box', padding: '10px 14px',
                background: 'none', border: 'none', borderRadius: 0,
                color: t.accent, cursor: 'pointer', textDecoration: 'none', transition: TOKENS.trans,
              }}
            >
              <svg viewBox="0 0 200 44" preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                aria-hidden="true">
                <path d={sk(3, 3, 194, 38, 9)} stroke={t.accent} strokeWidth={hovered ? 1.8 : 1.4}
                  fill={hovered ? (isDark ? 'rgba(167,139,250,0.14)' : 'rgba(124,58,237,0.08)') : 'none'}
                  opacity={0.85} style={{ transition: 'all 0.3s ease' }} />
              </svg>
              <span style={{ position: 'relative', fontFamily: 'var(--font-script, "Caveat", cursive)', fontSize: 22, lineHeight: 1 }}>
                {pct > 0 ? `Continue · ${pct}%` : 'Start Writing'}
              </span>
              <ArrowRight size={14} style={{ position: 'relative', transform: hovered ? 'translateX(2px)' : 'none', transition: TOKENS.fast }} />
            </Wrapper>
          );
        })()}
      </div>
    </div>
  );
};

export const SHAPE    = 'pencil';
export const SKILL_ID = 'copywriting';

export default CopywritingCard;
